import { FieldType, FcaResponseResult, Address, FirmFcaData, LenderFirmDraftDto, FirmGetById, FirmGetAll, FirmOperationRequest, FirmType, OperationType } from '@pmp/api';
import { TFunction } from 'i18next';
import { NotificationInstance } from 'antd/es/notification/interface';
import styles from './styles.module.css';

export const FIRMS_ENTITY_LIST_NAMES: FieldType[] = [
  FieldType.EXCLUSIVITY_SETS,
  FieldType.FIRM_TYPE,
  FieldType.YES_NO,
  FieldType.COMPLIANCE_STATUS,
  FieldType.OPERATION_TYPE,
  FieldType.ACTIVITY_TYPE,
  FieldType.FIRMS,
  FieldType.PRINCIPALS,
  FieldType.ADVISORS,
  FieldType.MORTGAGE_CLUBS,
  FieldType.PACKAGERS,
];

export const OPERATION_ORDER = ['ADVICE', 'PACKAGE', 'NETWORK', 'MORTGAGE_CLUB'];

export const operationToFieldTypeMap: Record<string, FieldType> = {
  NETWORK: FieldType.PRINCIPALS,
  ADVICE: FieldType.ADVISORS,
  PACKAGE: FieldType.PACKAGERS,
  MORTGAGE_CLUB: FieldType.MORTGAGE_CLUBS,
};

// export const getFirmsOptions = (firms: FirmResponse[]): ValueList =>
//   firms.reduce((acc: ValueList, firm: FirmResponse) => ({ ...acc, [firm.id]: fee.name ?? firm.id }), {} as ValueList);

export const FCA_RESULT_MESSAGES: Partial<Record<FcaResponseResult, string>> = {
  [FcaResponseResult.PARTIAL_DATA]: 'Some FCA data could not be loaded.',
  [FcaResponseResult.REFERENCE_NOT_FOUND]: 'The FRN is incorrect. No firm found.',
  [FcaResponseResult.FCA_ERROR]: 'FCA returned an error. Please try again later.',
};

export const showFcaNotification = (result: FcaResponseResult, t: TFunction, notification: NotificationInstance) => {
  switch (result) {
    case FcaResponseResult.OK:
      notification.success({ message: t('lender.firms.labels.ok') });
      break;
    case FcaResponseResult.PARTIAL_DATA:
      notification.warning({ message: t('lender.firms.labels.partialData') });
      break;
    case FcaResponseResult.REFERENCE_NOT_FOUND:
      notification.warning({ message: t('lender.firms.labels.referenceNotFound') });
      break;
    case FcaResponseResult.FCA_ERROR:
      notification.error({ message: t('lender.firms.labels.fcaError') });
      break;
  }
};

export const formatAddressAsLines = (addr: Address): string =>
  [addr?.lineItems?.filter(Boolean).join(' '), [addr?.city, addr?.postCode, addr?.country].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join('\n');

export const mapLenderFirmFormValuesToDraft = (values: FirmGetById, record: FirmGetAll | null | undefined): LenderFirmDraftDto => {
  const { registrationDetails, contactDetails, payments, packaging, exclusives } = values;

  const operationsWhitelist: FirmOperationRequest[] = (registrationDetails?.operations ?? []).map(
    (op: OperationType) => ({
      type: op,
      bankAccountDetails: payments?.operations?.[op]
        ? {
            accountName: payments.operations[op]?.accountName ?? null,
            accountNumber: payments.operations[op]?.accountNumber ?? null,
            sortCode: payments.operations[op]?.sortCode ?? null,
          }
        : null,
    }),
  );

  return {
    id: values.firmId ?? record?.id ?? null,
    ohpenId: registrationDetails?.ohpenId ?? record?.ohpenId ?? '',
    name: registrationDetails?.name ?? '',
    type: registrationDetails?.type ?? FirmType.NOT_FCA_REGISTERED,
    frn: registrationDetails?.type === FirmType.NOT_FCA_REGISTERED ? null : (registrationDetails?.frn != null ? Number(registrationDetails.frn) : null),
    principalFirmIds: registrationDetails?.principalIds ?? [],
    isActive: registrationDetails?.isActive ?? null,
    nacfbNumber: registrationDetails?.nacfbNumber ?? null,
    reference: registrationDetails?.reference ?? null,
    activitiesWhitelist: registrationDetails?.activitiesPermissions ?? [],
    operationsWhitelist,
    defaultPaymentRouteFirmId: payments?.defaultPaymentRouteFirmId ?? null,
    paymentRouteFirmIds: payments?.paymentRouteFirmIds ?? null,
    packagerFirmIds: packaging?.packagerFirmIds ?? [],
    idsOfExclusivitySets: exclusives ?? [],
    qualifiedDomains: contactDetails?.qualifiedDomains ?? null,
    email: contactDetails?.email ?? null,
    mobilePhone: contactDetails?.mobilePhone ?? null,
    workPhone: contactDetails?.workPhone ?? null,
    website: contactDetails?.website ?? null,
    complaintsWebsite: contactDetails?.complaintsWebsite ?? null,
    firmAddress: contactDetails?.firmAddress ?? null,
    lockedByActionId: values.lockedByActionId ?? null,
  };
};

export const getHighlightProps = (dataIndex: string[], fcaModifiedFields: FirmFcaData | null | undefined) => {
  if (!fcaModifiedFields) return '';
  const tab = dataIndex[0] as keyof FirmFcaData;
  const field = dataIndex[1] as keyof FirmFcaData[typeof tab];

  const isChanged = fcaModifiedFields[tab]?.[field] !== undefined || (tab === 'tradingNames' && fcaModifiedFields[tab] !== undefined); // Highlight all trading name fields if any have changed, since we don't track individual field changes within trading names
  return isChanged ? styles.highlightedField : '';
};
